import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Header from "./components/Header";
import ContactFab from "./components/ContactFab";
import Landing from "./pages/Landing";
import QuoteForm from "./pages/QuoteForm";
import ClientStatus from "./pages/ClientStatus";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

function App() {
  useEffect(() => {
    let lastFocused = null;

    const isEditable = (el) => {
      if (!el || !(el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return false;
      if (el.type === "file" || el.type === "hidden" || el.disabled || el.readOnly) return false;
      return true;
    };

    // Bring the given element above the soft keyboard / sticky header
    const ensureVisible = (el) => {
      if (!el || !document.contains(el)) return;
      const vv = window.visualViewport;
      const rect = el.getBoundingClientRect();
      const visibleTop = vv ? vv.offsetTop : 0;
      const visibleHeight = vv ? vv.height : window.innerHeight;
      const visibleBottom = visibleTop + visibleHeight;
      const headerOffset = 80;   // leave room for sticky header
      const bottomPad = 24;      // breathing room above keyboard

      if (rect.bottom > visibleBottom - bottomPad) {
        // Field is hidden behind the keyboard — scroll up just enough
        window.scrollBy({ top: rect.bottom - (visibleBottom - bottomPad), behavior: "smooth" });
      } else if (rect.top < visibleTop + headerOffset) {
        // Field is hidden behind the sticky header — scroll down a touch
        window.scrollBy({ top: rect.top - (visibleTop + headerOffset), behavior: "smooth" });
      }
    };

    const onFocusIn = (e) => {
      const el = e.target;
      if (!isEditable(el)) return;
      lastFocused = el;
      // First nudge: layout viewport (works on desktop)
      setTimeout(() => ensureVisible(el), 50);
      // Second nudge: after the soft keyboard finishes opening
      setTimeout(() => ensureVisible(el), 400);
    };

    const onFocusOut = () => { lastFocused = null; };

    // When the visual viewport resizes (= keyboard opens/closes) re-check the focused field
    const onViewportResize = () => {
      if (lastFocused && document.activeElement === lastFocused) {
        setTimeout(() => ensureVisible(lastFocused), 50);
      }
    };

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onViewportResize);
      window.visualViewport.addEventListener("scroll", onViewportResize);
    }
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onViewportResize);
        window.visualViewport.removeEventListener("scroll", onViewportResize);
      }
    };
  }, []);

  return (
    <div className="App min-h-screen bg-[#F9F8F6]" data-testid="app-root">
      <BrowserRouter>
        <Header />
        <Toaster richColors position="top-center" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/quote" element={<QuoteForm />} />
          <Route path="/status" element={<ClientStatus />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ContactFab />
      </BrowserRouter>
    </div>
  );
}

export default App;
